import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from 'react-modal';
import { useUnsavedChanges } from '@connect-x-app/hybrid-state';
import { Text } from '@connect-x-app/hybrid-ui';
import { Keyword, SmartAction } from '@connect-x-app/shared-domain-models';
import { COLORS } from '@connect-x-app/shared-ui';
import { Response } from '@connect-x-app/web-state';
import { Checkbox, IconWithTooltip, InputField } from '@connect-x-app/web-ui';

import { AddButton, EmptyState, FlexComponent, PageContentBlock, SaveButton, TableLayout } from '../../../../../shared';
import { customModalStyles } from '../../../../../shared/modal/ModalUtils';
import { useKeywordTable } from '../hooks/useKeywordTable';
import { AddKeywordModal } from './AddKeywordModal';

type Props = {
  canSave: boolean;
  form: Response<SmartAction, SmartAction>;
  isSystemManaged?: boolean;
};

export const SmartActionForm = ({ canSave, form, isSystemManaged = false }: Props) => {
  const { t } = useTranslation();

  const [isModalOpen, setIsModalOpen] = useState(false);

  const { deleteKeyword, isEmpty, selectedKeywords, table } = useKeywordTable({ form, renderCheckbox: canSave });
  const { setHasUnsavedChanges } = useUnsavedChanges();

  const addingKeyword = (keyword: Keyword) => {
    form.setAttribute([...(form.values.keywords ?? []), keyword], 'keywords');
    setIsModalOpen(false);
  };

  const onChangeInternal = (value: unknown, name: string) => {
    form.setAttribute(value, name);
  };

  useEffect(() => {
    setHasUnsavedChanges(form.isDirty);
  }, [form.isDirty, setHasUnsavedChanges]);

  // Mirrors the useCallback shape SmartActions.tsx uses: TableLayout renders `emptyState` as a
  // component, so it must keep a stable identity across renders rather than be defined inline.
  const emptyStateButton = useCallback(() => {
    if (!canSave) {
      return null;
    }
    return <AddButton label={t('SMART_ACTION_FORM_KEYWORDS_ADD_BUTTON')} onClick={() => setIsModalOpen(true)} />;
  }, [canSave, t]);

  const emptyState = useCallback(() => {
    return (
      <EmptyState
        button={emptyStateButton}
        description={t('SMART_ACTION_FORM_KEYWORDS_EMPTY_DESCRIPTION')}
        title={t('SMART_ACTION_FORM_KEYWORDS_EMPTY_TITLE')}
      />
    );
  }, [emptyStateButton, t]);

  const nameField = (
    <InputField
      className="smart-action-container__input"
      disabled={!canSave}
      label={t('SMART_ACTION_FORM_SMART_ACTION_NAME')}
      name="name"
      onChange={onChangeInternal}
      required
      showInlineBottomError
      validation={form.validationErrors.name}
      value={form.values?.name || ''}
    />
  );

  const urlField = (
    <InputField
      className="smart-action-container__input"
      disabled={!canSave}
      label={t('SMART_ACTION_FORM_URL')}
      name="url"
      onChange={onChangeInternal}
      required
      showInlineBottomError
      validation={form.validationErrors.url}
      value={form.values?.url ?? ''}
    />
  );

  const apiKeyField = (
    <InputField
      className="smart-action-container__input"
      disabled={!canSave}
      label={t('SMART_ACTION_FORM_APIKEY')}
      name="apiKey"
      onChange={onChangeInternal}
      value={form.values?.apiKey ?? ''}
    />
  );

  const descriptionField = (
    <InputField
      className="smart-action-container__input"
      disabled={!canSave}
      label={t('SMART_ACTION_FORM_DESCRIPTION')}
      name="description"
      onChange={onChangeInternal}
      showInlineBottomError
      validation={form.validationErrors.description}
      value={form.values?.description ?? ''}
    />
  );

  return (
    <>
      <Modal isOpen={isModalOpen} onRequestClose={() => setIsModalOpen(false)} style={customModalStyles}>
        <AddKeywordModal closeModal={() => setIsModalOpen(false)} onsubmit={addingKeyword} />
      </Modal>
      <form onSubmit={form.submit}>
        <PageContentBlock
          rightButton={canSave ? <SaveButton isDisabled={!form.isDirty || !form.isValid} /> : null}
          title={t('SMART_ACTION_FORM_SETTINGS')}
        >
          <FlexComponent flexDirection="column" gap={1} width="full">
            <FlexComponent flexDirection="row" gap={1} width="full">
              {nameField}
              {isSystemManaged ? descriptionField : urlField}
            </FlexComponent>
            {isSystemManaged ? null : (
              <FlexComponent flexDirection="row" gap={1} width="full">
                {apiKeyField}
                {descriptionField}
              </FlexComponent>
            )}
            <div className="smart-action-request-response-row">
              <div className="checkbox-container">
                <Checkbox
                  checked={form.values?.isRequestPublic}
                  disabled={!canSave}
                  label={t('SMART_ACTION_FORM_PUBLIC_REQUEST')}
                  name="isRequestPublic"
                  onChange={onChangeInternal}
                />
                <IconWithTooltip iconName={'circularInfoOutline'} label={t('SMART_ACTION_FORM_PUBLIC_REQUEST_TOOLTIP')} />
              </div>
              <div className="checkbox-container">
                <Checkbox
                  checked={form.values?.isResponsePublic}
                  disabled={!canSave}
                  label={t('SMART_ACTION_FORM_PUBLIC_RESPONSE')}
                  name="isResponsePublic"
                  onChange={onChangeInternal}
                />
                <IconWithTooltip iconName={'circularInfoOutline'} label={t('SMART_ACTION_FORM_PUBLIC_RESPONSE_TOOLTIP')} />
              </div>
            </div>
          </FlexComponent>
        </PageContentBlock>
      </form>
      <div className="title-subtitle">
        <Text color={COLORS.zulu.$08} size="XL" weight="regular">
          {t('SMART_ACTION_FORM_KEYWORDS')}
        </Text>
        <Text color={COLORS.zulu.$08} size="SM" weight="regular">
          {t('SMART_ACTION_FORM_KEYWORDS_INFO_TABLE')}
        </Text>
      </div>
      <TableLayout
        addButtonAction={() => setIsModalOpen(true)}
        addButtonLabel={t('SMART_ACTION_FORM_KEYWORDS_ADD_BUTTON')}
        deleteButtonAction={deleteKeyword}
        deleteButtonDisabled={!selectedKeywords?.length}
        emptyState={emptyState}
        isAddButtonPresent={!isEmpty && canSave}
        isDeleteButtonPresent={!isEmpty && canSave}
        table={table}
      />
    </>
  );
};
